import React from 'react';
import './aboutus.css';
import { 
  Activity, ShieldCheck, Smartphone, Globe, 
  Award, Stethoscope, UserCheck, Leaf, 
  ArrowRight, Quote, CheckCircle2 
} from 'lucide-react';

const AboutHcura = () => {
  return (
    <div className="hcura-master">
      
      {/* --- HERO SECTION: CORPORATE & CARE --- */}
      <header className="hero-section">
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <div className="hero-badge">Est. 2021 • Bengaluru, India</div>
          <h1>
            Bridging Ancient Wisdom with <br />
            <span className="text-gold">Modern Digital Healthcare.</span>
          </h1>
          <p className="hero-lead">
            H-Cura is more than a clinic; it is a movement. We combine the precision of 
            <strong> Advanced Diagnostics</strong>, the aesthetics of <strong>Modern Cosmetology</strong>, 
            and the deep healing of <strong>Homeopathy</strong>—all powered by a secure digital platform.
          </p>
        </div>
      </header>

      {/* --- STATS BAR --- */}
      <div className="stats-bar">
        <div className="container stats-flex">
          <div className="stat-box">
            <span className="stat-num">2021</span>
            <span className="stat-label">Year Founded</span>
          </div>
          <div className="stat-box">
            <span className="stat-num">100%</span>
            <span className="stat-label">Data Privacy</span>
          </div>
          <div className="stat-box">
            <span className="stat-num">Hybrid</span>
            <span className="stat-label">Online & Offline</span>
          </div>
          <div className="stat-box">
            <span className="stat-num">Ayush</span>
            <span className="stat-label">Certified Experts</span>
          </div>
        </div>
      </div>

      {/* --- THE ORIGIN STORY --- */}
      <section className="section-origin">
        <div className="container grid-2">
          <div className="text-col">
            <h5 className="sub-head">Our Origins</h5>
            <h2 className="section-head">Connecting Care for Cure</h2>
            <div className="gold-divider"></div>
            <p>
              The story of H-Cura began in 2021, amidst a global health crisis that highlighted a critical gap: 
              the disconnect between patients and reliable, holistic medicine.
            </p>
            <p>
              Headquartered in <strong>Bengaluru</strong>, H-CURA PRIVATE LIMITED was born with a singular mission: 
              to democratize access to Indian Homeopathy. What started as a digital bridge has evolved into a 
              <strong> multispeciality ecosystem </strong> where patients receive integrated care—treating the 
              tooth pain, the skin rash, and the internal imbalance simultaneously.
            </p>
            <div className="quote-box">
              <Quote className="quote-icon" size={24} />
              <p>"We don't just treat symptoms. We build immunity, confidence, and smiles."</p>
            </div>
          </div>
          <div className="image-col">
            <div className="image-card main-img">
              {/* Replace with clinic image */}
              <img src="https://images.unsplash.com/photo-1666214280557-f1b5022eb634?q=80&w=2070&auto=format&fit=crop" alt="Hcura Clinic" />
            </div>
            <div className="image-card float-img">
               {/* Replace with App screenshot or Tech image */}
              <img src="https://images.unsplash.com/photo-1576091160550-2187d80a1b95?q=80&w=2070&auto=format&fit=crop" alt="Hcura Tech" />
            </div>
          </div>
        </div>
      </section>

      {/* --- THE TWO PILLARS (TECH + CLINIC) --- */}
      <section className="section-pillars">
        <div className="container">
          <div className="pillar-header">
            <h2>The H-Cura Ecosystem</h2>
            <p>We operate at the intersection of Technology and Biology.</p>
          </div>
          
          <div className="pillar-grid">
            {/* Pillar 1: The Clinic */}
            <div className="pillar-card clinic">
              <div className="icon-circle"><Stethoscope size={32}/></div>
              <h3>The Physical Clinic</h3>
              <p>A state-of-the-art facility in Bengaluru offering:</p>
              <ul className="check-list">
                <li><CheckCircle2 size={16}/> <strong>Advanced Hair & Skin:</strong> Laser, PRP, Anti-Aging.</li>
                <li><CheckCircle2 size={16}/> <strong>Precision Dental:</strong> Implants, Aligners, Smile Design.</li>
                <li><CheckCircle2 size={16}/> <strong>Clinical Homeopathy:</strong> Constitutional healing.</li>
              </ul>
            </div>

            {/* Pillar 2: The Tech */}
            <div className="pillar-card tech">
              <div className="icon-circle"><Smartphone size={32}/></div>
              <h3>The Digital Platform</h3>
              <p>An end-to-end encrypted App ensuring access anywhere:</p>
              <ul className="check-list">
                <li><CheckCircle2 size={16}/> <strong>Tele-Consultations:</strong> Connect with top doctors instantly.</li>
                <li><CheckCircle2 size={16}/> <strong>Data Security:</strong> Your records are private and encrypted.</li>
                <li><CheckCircle2 size={16}/> <strong>Smart Matching:</strong> Find the right specialist for your need.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* --- CORPORATE GOVERNANCE & VALUES --- */}
      <section className="section-governance">
        <div className="container">
          <div className="governance-box">
            <div className="gov-content">
              <h2>Corporate Governance</h2>
              <p>
                At H-Cura, our obligation extends beyond profit. We are accountable to every stakeholder: 
                Doctors, Patients, Associates, and the Government. We operate with strict adherence to 
                <strong> AYUSH Ministry guidelines</strong>, ensuring:
              </p>
              <div className="values-row">
                <div className="value-item">
                  <ShieldCheck className="v-icon" />
                  <span>Integrity</span>
                </div>
                <div className="value-item">
                  <UserCheck className="v-icon" />
                  <span>Transparency</span>
                </div>
                <div className="value-item">
                  <Globe className="v-icon" />
                  <span>Inclusivity</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- THE 5 S STRATEGY --- */}
      <section className="section-5s">
        <div className="container">
          <h2 className="center-title">The "5 S" Digital Promise</h2>
          <div className="s-grid">
            <div className="s-card">
              <h3>Simple</h3>
              <p>User-friendly interface designed for all ages.</p>
            </div>
            <div className="s-card">
              <h3>Smart</h3>
              <p>AI-assisted matching for faster diagnosis.</p>
            </div>
            <div className="s-card">
              <h3>Safe</h3>
              <p>End-to-End Encryption for total privacy.</p>
            </div>
            <div className="s-card">
              <h3>Secure</h3>
              <p>Zero unauthorized data access policy.</p>
            </div>
            <div className="s-card">
              <h3>Sustainable</h3>
              <p>Constantly evolving eco-friendly tech.</p>
            </div>
          </div>
        </div>
      </section>

      {/* --- AWARDS & MAGAZINE --- */}
      <section className="section-awards">
        <div className="container flex-row-award">
          <div className="award-text">
            <h4>Recognized Excellence</h4>
            <h2>Featured in Siliconindia</h2>
            <p>
              We are proud to be recognized as a leading innovator in the 
              <strong> "STARTUP City"</strong> feature (April 2023), acknowledging our 
              contribution to modernizing homeopathic care.
            </p>
            <button className="btn-outline">Read Feature <ArrowRight size={16} /></button>
          </div>
          <div className="award-badge-container">
            <div className="award-seal">
              <Award size={40} color="#fff" />
              <span>Startup City<br/>2023</span>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutHcura;